import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { getDocumentTypeLabel } from "@/documents/utils/documentTree";
import {
  showFolderImportErrorToast,
  showFolderImportInProgressToast,
  showFolderImportSuccessToast,
} from "@/documents/utils/downloadToast";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { useMutateData } from "@/hooks/useMutation";
import usePostMultipart from "@/hooks/usePostMultipart";
import { ProgramType } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { FolderInput } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PROGRAM_ROOT_DEFAULT_KEY_PREFIX = "program-";
const PROJECT_DOC_TYPES = [2, 3, 7, 8];
const INTERVENANT_DOC_TYPES = [1, 5, 4];
const CONTRACTUAL_DOC_TYPES = [25, 26, 27];
const REPORT_DOC_TYPES = [10, 11, 12, 13, 14, 15];
const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"];
const MAX_UPLOAD_BATCH_BYTES = 80 * 1024 * 1024;
const MAX_UPLOAD_BATCH_FILES = 25;
const PORTEUR_ROOT_DEFAULT_LABEL = "Documents administratifs";

type DocumentFolderTreeResponse = {
  porteur?: Record<string, FolderNode[]>;
  project?: Record<string, FolderNode[]>;
  intervenant?: Record<string, FolderNode[]>;
};

type FolderNode = {
  id: number;
  name?: string | null;
  defaultKey?: string | null;
  children?: FolderNode[];
};

type FolderImport = {
  name: string;
  files: File[];
  directoryPaths?: string[];
};

type ResolvedDefaultFolderTarget = {
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  defaultKey?: string | null;
  folderName: string;
};

type ImportDocumentFolderButtonProps = {
  externalImport?: FolderImport | null;
  hideTrigger?: boolean;
  onExternalImportConsumed?: () => void;
  ownerId?: number | null;
};

const hasSameValues = (values: number[], expectedValues: number[]) => {
  const left = [...new Set(values)].sort((a, b) => a - b);
  const right = [...new Set(expectedValues)].sort((a, b) => a - b);

  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const getInvalidFiles = (files: File[]) =>
  files.filter((file) => {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return !ALLOWED_EXTENSIONS.includes(extension);
  });

const getFolderNameFromFiles = (files: File[]) => {
  const firstRelativePath = (files[0] as File & { webkitRelativePath?: string } | undefined)?.webkitRelativePath ?? "";
  return firstRelativePath.split("/").filter(Boolean)[0] ?? "Dossier importé";
};

const getRelativePathSegments = (file: File, folderName: string) => {
  const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || `${folderName}/${file.name}`;
  const segments = relativePath.split("/").filter(Boolean);

  if (segments[0] === folderName) {
    return segments.slice(1);
  }

  return segments;
};

const defineRelativePath = (file: File, relativePath: string) => {
  Object.defineProperty(file, "webkitRelativePath", {
    value: relativePath,
    configurable: true,
  });

  return file;
};

const splitFilesIntoUploadBatches = (files: File[]) => {
  const batches: File[][] = [];
  let currentBatch: File[] = [];
  let currentBatchSize = 0;

  files.forEach((file) => {
    const fileSize = Number(file.size ?? 0);
    const shouldStartNewBatch =
      currentBatch.length > 0 &&
      (
        currentBatch.length >= MAX_UPLOAD_BATCH_FILES ||
        currentBatchSize + fileSize > MAX_UPLOAD_BATCH_BYTES
      );

    if (shouldStartNewBatch) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBatchSize = 0;
    }

    currentBatch.push(file);
    currentBatchSize += fileSize;
  });

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

const getAuthHeaders = (): HeadersInit => {
  const token =
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fetchJsonWithAuth = async <T,>(endpoint: string): Promise<T | null> => {
  const response = await fetch(endpoint, {
    headers: getAuthHeaders(),
    credentials: "include",
  });

  if (!response.ok) {
    return null;
  }

  return response.json();
};

const getDocumentTypesFromDefaultKey = (defaultKey?: string | null) => {
  if (!defaultKey) {
    return [];
  }

  if (defaultKey.startsWith("type-")) {
    const documentType = Number(defaultKey.replace("type-", ""));
    return Number.isFinite(documentType) && documentType > 0 ? [documentType] : [];
  }

  if (defaultKey === "contractuel") {
    return CONTRACTUAL_DOC_TYPES;
  }

  if (defaultKey === "rapport") {
    return REPORT_DOC_TYPES;
  }

  return [];
};

const ImportDocumentFolderButton = ({
  externalImport,
  hideTrigger = false,
  onExternalImportConsumed,
  ownerId = null,
}: ImportDocumentFolderButtonProps) => {
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const queryClient = useQueryClient();
  const { postMultipart } = usePostMultipart();
  const documentFoldersTreeEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/document-folders/tree`;
  const carrierDocumentsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/docs/carrier`;
  const { data: customFolders } = useFetchDataV2<DocumentFolderTreeResponse>(
    documentFoldersTreeEndpoint,
  );
  const { data: programs } = useFetchDataV2<ProgramType[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
  );
  const { mutateAsync: saveFolderAsync } = useMutateData({
    method: "POST",
    id: `document-folder-import-save`,
  });
  const { mutateAsync: attachMediaAsync } = useMutateData({
    method: "POST",
    id: `document-folder-import-attach`,
  });
  const { mutateAsync: hideDefaultFolderAsync } = useMutateData({
    method: "POST",
    id: `document-folder-import-hide-default`,
  });

  const showInvalidFileToast = (files: File[]) => {
    if (files.length === 0) return;

    toast({
      title:
        files.length === 1
          ? `Type de fichier invalide : ${files[0].name}`
          : `${files.length} fichiers ont un type invalide.`,
      variant: "destructive",
      duration: 4000,
    });
  };

  const findFolderById = (folders: FolderNode[] = [], folderId: number): FolderNode | null => {
    for (const folder of folders) {
      if (Number(folder.id) === folderId) {
        return folder;
      }

      const nested = findFolderById(folder.children ?? [], folderId);
      if (nested) return nested;
    }

    return null;
  };

  const findDirectFolderByName = (folders: FolderNode[] = [], name: string): FolderNode | null => {
    const normalizedName = name.trim();
    return folders.find((folder) => (folder.name ?? "").trim() === normalizedName) ?? null;
  };

  const findFolderByDefaultKey = (folders: FolderNode[] = [], defaultKey?: string | null): FolderNode | null => {
    if (!defaultKey) {
      return null;
    }

    for (const folder of folders) {
      if ((folder.defaultKey ?? null) === defaultKey) {
        return folder;
      }

      const nested = findFolderByDefaultKey(folder.children ?? [], defaultKey);
      if (nested) return nested;
    }

    return null;
  };

  const getProjectMediaIds = (projectId: number) =>
    (programs ?? [])
      .flatMap((program: any) => program?.projects ?? [])
      .find((project: any) => Number(project?.id) === Number(projectId))
      ?.mediaDescription
      ?.map((media: any) => Number(media?.id))
      .filter((mediaId: number) => Number.isFinite(mediaId) && mediaId > 0) ?? [];

  const resolveDefaultFolderTarget = (): ResolvedDefaultFolderTarget | null => {
    const projectIds = (searchParams.get("projects") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const programIds = (searchParams.get("programs") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const intervenantIds = (searchParams.get("intervenants") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const categoryIds = (searchParams.get("categories") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const selectedTypes = (searchParams.get("types") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const scope = searchParams.get("scope");
    const folderIds = (searchParams.get("folderIds") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const pathParts = window.location.pathname.split("/");
    const projectPathId =
      pathParts[1] === "dashboard" && pathParts[2] === "project"
        ? Number(pathParts[3])
        : null;
    const currentOwnerId = Number(ownerId ?? 0);

    if (projectIds.length === 1 || (projectPathId && Number.isFinite(projectPathId) && projectPathId > 0)) {
      const projectScopeId = projectIds[0] ?? projectPathId;

      if (selectedTypes.includes("image")) {
        return { scopeType: "project", scopeId: projectScopeId, defaultKey: "photos", folderName: "Photos" };
      }

      if (categoryIds.length === 1 && PROJECT_DOC_TYPES.includes(categoryIds[0]!)) {
        return {
          scopeType: "project",
          scopeId: projectScopeId,
          defaultKey: `type-${categoryIds[0]}`,
          folderName: getDocumentTypeLabel(categoryIds[0]!),
        };
      }

      return { scopeType: "project", scopeId: projectScopeId, defaultKey: "root", folderName: "Projet" };
    }

    if (intervenantIds.length === 1) {
      if (categoryIds.length === 1 && INTERVENANT_DOC_TYPES.includes(categoryIds[0]!)) {
        return {
          scopeType: "intervenant",
          scopeId: intervenantIds[0],
          defaultKey: `type-${categoryIds[0]}`,
          folderName: getDocumentTypeLabel(categoryIds[0]!),
        };
      }

      if (hasSameValues(categoryIds, CONTRACTUAL_DOC_TYPES)) {
        return { scopeType: "intervenant", scopeId: intervenantIds[0], defaultKey: "contractuel", folderName: "Contractuel" };
      }

      if (hasSameValues(categoryIds, REPORT_DOC_TYPES)) {
        return { scopeType: "intervenant", scopeId: intervenantIds[0], defaultKey: "rapport", folderName: "Rapport" };
      }

      return { scopeType: "intervenant", scopeId: intervenantIds[0], defaultKey: "root", folderName: "Intervenant" };
    }

    if (!currentOwnerId) return null;

    const isDocumentsRootView =
      !scope &&
      projectIds.length === 0 &&
      programIds.length === 0 &&
      intervenantIds.length === 0 &&
      categoryIds.length === 0 &&
      selectedTypes.length === 0 &&
      folderIds.length === 0;

    if (isDocumentsRootView) {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: null, folderName: "" };
    }

    if (scope === "projects" && programIds.length === 0) {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "projects-root", folderName: "Mes Programmes" };
    }

    if (programIds.length === 1 && !searchParams.get("projects")) {
      const program = programs?.find((item) => Number(item?.id) === programIds[0]);
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: `${PROGRAM_ROOT_DEFAULT_KEY_PREFIX}${programIds[0]}`,
        folderName: program?.name ?? "Programme",
      };
    }

    if (categoryIds.length === 1 && INTERVENANT_DOC_TYPES.includes(categoryIds[0]!)) {
      return {
        scopeType: "porteur",
        scopeId: currentOwnerId,
        defaultKey: `type-${categoryIds[0]}`,
        folderName: getDocumentTypeLabel(categoryIds[0]!),
      };
    }

    if (hasSameValues(categoryIds, CONTRACTUAL_DOC_TYPES)) {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "contractuel", folderName: "Contractuel" };
    }

    if (hasSameValues(categoryIds, REPORT_DOC_TYPES)) {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "rapport", folderName: "Rapport" };
    }

    if (scope === "intervenants") {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "intervenants-root", folderName: "Dossier intervenant" };
    }

    if (scope === "porteur") {
      return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "root", folderName: PORTEUR_ROOT_DEFAULT_LABEL };
    }

    return { scopeType: "porteur", scopeId: currentOwnerId, defaultKey: "root", folderName: PORTEUR_ROOT_DEFAULT_LABEL };
  };

  const ensureCurrentParentFolderId = async (): Promise<number | null> => {
    const folderIds = (searchParams.get("folderIds") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (folderIds.length === 1) {
      return folderIds[0];
    }

    const target = resolveDefaultFolderTarget();
    if (!target?.defaultKey) {
      return null;
    }

    const scopedFolders = customFolders?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const existingFolder = findFolderByDefaultKey(scopedFolders, target.defaultKey);
    if (existingFolder?.id) {
      await hideDefaultFolderAsync({
        endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
        body: {
          scopeType: target.scopeType,
          scopeId: target.scopeId,
          defaultKey: target.defaultKey,
        },
      });

      return Number(existingFolder.id);
    }

    const savedTree = await saveFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
      body: {
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        defaultKey: target.defaultKey,
        reuseByDefaultKey: true,
        name: target.folderName,
      },
    });

    await hideDefaultFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
      body: {
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        defaultKey: target.defaultKey,
      },
    });

    const savedScopedFolders =
      (savedTree as DocumentFolderTreeResponse | undefined)?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const savedFolder = findFolderByDefaultKey(savedScopedFolders, target.defaultKey);

    return Number(savedFolder?.id ?? 0) || null;
  };

  const createImportedFolder = async (name: string, parentId: number | null): Promise<number | null> => {
    const target = resolveDefaultFolderTarget();
    const trimmedName = name.trim() || "Dossier importé";
    if (!target) return null;

    const savedTree = await saveFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
      body: {
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        parentId,
        ...(!parentId && getDocumentTypesFromDefaultKey(target.defaultKey).length > 0
          ? { documentTypes: getDocumentTypesFromDefaultKey(target.defaultKey) }
          : {}),
        name: trimmedName,
      },
    });

    const directFolderId =
      Number((savedTree as any)?.id ?? 0) ||
      Number((savedTree as any)?.data?.id ?? 0) ||
      Number((savedTree as any)?.folder?.id ?? 0);
    if (directFolderId) {
      return directFolderId;
    }

    const scopedFolders =
      (savedTree as DocumentFolderTreeResponse | undefined)?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const parentFolder = parentId ? findFolderById(scopedFolders, parentId) : null;
    const createdFolder = parentFolder
      ? findDirectFolderByName(parentFolder.children ?? [], trimmedName)
      : findDirectFolderByName(scopedFolders, trimmedName);

    if (createdFolder?.id) {
      return Number(createdFolder.id);
    }

    const freshTree = await fetchJsonWithAuth<DocumentFolderTreeResponse>(
      documentFoldersTreeEndpoint,
    );
    const freshScopedFolders = freshTree?.[target.scopeType]?.[String(target.scopeId)] ?? [];
    const freshParentFolder = parentId ? findFolderById(freshScopedFolders, parentId) : null;
    const freshCreatedFolder = freshParentFolder
      ? findDirectFolderByName(freshParentFolder.children ?? [], trimmedName)
      : findDirectFolderByName(freshScopedFolders, trimmedName);

    return Number(freshCreatedFolder?.id ?? 0) || null;
  };

  const uploadFileBatch = async (files: File[]) => {
    const formData = new FormData();
    formData.append("type", "2");
    formData.append("description", "");

    files.forEach((file) => {
      formData.append("files[]", file);
    });

    const newMedias = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data: formData,
    });

    if (!Array.isArray(newMedias)) {
      throw new Error(newMedias?.message || "Erreur lors de l’import du dossier.");
    }

    return newMedias;
  };

  const uploadFiles = async (files: File[]) => {
    const uploadedMedias = [];
    const batches = splitFilesIntoUploadBatches(files);

    for (const batch of batches) {
      const newMedias = await uploadFileBatch(batch);
      uploadedMedias.push(...newMedias);
    }

    return uploadedMedias;
  };

  const attachFilesToFolder = async (
    folderId: number,
    mediaIds: number[],
    projectMediaIdsRef: { current: number[] },
  ) => {
    if (!folderId || mediaIds.length === 0) {
      return;
    }

    const target = resolveDefaultFolderTarget();
    if (target?.scopeType === "project") {
      const nextProjectMediaIds = [...new Set([...projectMediaIdsRef.current, ...mediaIds])];

      await attachMediaAsync({
        endpoint: `${import.meta.env.VITE_API_ENDPOINT}/project/edit/${target.scopeId}`,
        body: {
          files: nextProjectMediaIds,
          documentFolderId: folderId,
          otherDocumentMediaIds: mediaIds,
        },
      });
      projectMediaIdsRef.current = nextProjectMediaIds;
      return;
    }

    await attachMediaAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/attach-media`,
      body: {
        folderId,
        mediaIds,
      },
    });
  };

  const importFolder = async (folderName: string, files: File[], directoryPaths: string[] = []) => {
    if (isImporting) return;

    const invalidFiles = getInvalidFiles(files);
    if (invalidFiles.length > 0) {
      showInvalidFileToast(invalidFiles);
    }

    const validFiles = files.filter((file) => !invalidFiles.includes(file));
    if (validFiles.length === 0) {
      return;
    }

    setIsImporting(true);
    const importingToast = showFolderImportInProgressToast({
      folderName,
    });

    try {
      const parentFolderId = await ensureCurrentParentFolderId();
      const importedFolderId = await createImportedFolder(folderName, parentFolderId);
      if (!importedFolderId) {
        throw new Error("Impossible de créer le dossier importé.");
      }

      const folderIdsByPath = new Map<string, number>([["", importedFolderId]]);
      const filesByFolderId = new Map<number, File[]>();
      const target = resolveDefaultFolderTarget();
      const projectMediaIdsRef = {
        current: target?.scopeType === "project" ? getProjectMediaIds(target.scopeId) : [],
      };

      const ensureNestedFolder = async (segments: string[]) => {
        let currentParentId = importedFolderId;
        const currentPath: string[] = [];

        for (const segment of segments) {
          currentPath.push(segment);
          const pathKey = currentPath.join("/");
          const cachedFolderId = folderIdsByPath.get(pathKey);

          if (cachedFolderId) {
            currentParentId = cachedFolderId;
            continue;
          }

          const createdFolderId = await createImportedFolder(segment, currentParentId);
          if (!createdFolderId) {
            throw new Error(`Impossible de créer le sous-dossier ${segment}.`);
          }

          folderIdsByPath.set(pathKey, createdFolderId);
          currentParentId = createdFolderId;
        }

        return currentParentId;
      };

      const allFolderPaths = new Set<string>();
      directoryPaths.forEach((path) => {
        const segments = path.split("/").filter(Boolean);
        if (segments[0] === folderName) {
          segments.shift();
        }
        if (segments.length > 0) {
          allFolderPaths.add(segments.join("/"));
        }
      });

      for (const file of validFiles) {
        const relativeSegments = getRelativePathSegments(file, folderName);
        const folderSegments = relativeSegments.slice(0, -1);
        if (folderSegments.length > 0) {
          allFolderPaths.add(folderSegments.join("/"));
        }
      }

      for (const folderPath of [...allFolderPaths].sort((a, b) => a.split("/").length - b.split("/").length)) {
        await ensureNestedFolder(folderPath.split("/").filter(Boolean));
      }

      for (const file of validFiles) {
        const relativeSegments = getRelativePathSegments(file, folderName);
        const folderSegments = relativeSegments.slice(0, -1);
        const targetFolderId = await ensureNestedFolder(folderSegments);
        const existingFiles = filesByFolderId.get(targetFolderId) ?? [];
        filesByFolderId.set(targetFolderId, [...existingFiles, file]);
      }

      for (const [folderId, folderFiles] of filesByFolderId.entries()) {
        const newMedias = await uploadFiles(folderFiles);
        await attachFilesToFolder(
          folderId,
          newMedias.map((media: any) => Number(media?.id)).filter(Boolean),
          projectMediaIdsRef,
        );
      }

      const [freshTree, freshDocuments] = await Promise.all([
        fetchJsonWithAuth<DocumentFolderTreeResponse>(documentFoldersTreeEndpoint),
        fetchJsonWithAuth<any[]>(carrierDocumentsEndpoint),
      ]);

      if (freshTree) {
        queryClient.setQueryData(["fetchDataV2", documentFoldersTreeEndpoint], freshTree);
      }

      if (freshDocuments) {
        queryClient.setQueryData(["fetchDataV2", carrierDocumentsEndpoint], freshDocuments);
      }

      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "fetchDataV2" &&
          typeof query.queryKey[1] === "string" &&
          (query.queryKey[1] === carrierDocumentsEndpoint ||
            query.queryKey[1] === documentFoldersTreeEndpoint),
      });
      importingToast.dismiss();
      showFolderImportSuccessToast({ folderName });
    } catch (error: any) {
      importingToast.dismiss();
      showFolderImportErrorToast({
        folderName,
        message: error?.message || "Impossible d’importer le dossier.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleFolderSelected = (files: FileList | null) => {
    const selectedFiles = Array.from(files ?? []);
    if (selectedFiles.length === 0) return;

    void importFolder(getFolderNameFromFiles(selectedFiles), selectedFiles);
  };

  const readDirectoryHandle = async (
    directoryHandle: any,
    rootName: string,
    currentPath = "",
  ): Promise<{ files: File[]; directoryPaths: string[] }> => {
    const files: File[] = [];
    const directoryPaths: string[] = [];

    for await (const [name, handle] of directoryHandle.entries()) {
      const relativePath = currentPath ? `${currentPath}/${name}` : name;

      if (handle.kind === "directory") {
        directoryPaths.push(relativePath);
        const nested = await readDirectoryHandle(handle, rootName, relativePath);
        files.push(...nested.files);
        directoryPaths.push(...nested.directoryPaths);
      } else if (handle.kind === "file") {
        const file = await handle.getFile();
        files.push(
          defineRelativePath(
            new File([file], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            }),
            `${rootName}/${relativePath}`,
          ),
        );
      }
    }

    return { files, directoryPaths };
  };

  const handleImportClick = async () => {
    const showDirectoryPicker = (window as any).showDirectoryPicker;
    if (typeof showDirectoryPicker !== "function") {
      inputRef.current?.click();
      return;
    }

    try {
      const directoryHandle = await showDirectoryPicker();
      const { files, directoryPaths } = await readDirectoryHandle(directoryHandle, directoryHandle.name);
      await importFolder(directoryHandle.name || "Dossier importé", files, directoryPaths);
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        toast({
          title: "Le dossier sélectionné n’a pas pu être ouvert.",
          description: "Veuillez choisir un autre dossier ou réessayer.",
          variant: "destructive",
          duration: 4000,
        });
      }
    }
  };

  useEffect(() => {
    if (!externalImport?.files?.length) return;

    void importFolder(externalImport.name, externalImport.files, externalImport.directoryPaths ?? []);
    onExternalImportConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalImport]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          handleFolderSelected(event.target.files);
          event.target.value = "";
        }}
        {...({ webkitdirectory: "", directory: "" } as any)}
      />
      {!hideTrigger && (
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-xl border-[#d9d1bb] bg-white text-[#5f5a49] hover:bg-[#f4f2ea]"
          onClick={handleImportClick}
          disabled={isImporting}
        >
          <FolderInput className="mr-2 h-4 w-4" />
          {isImporting ? "Import..." : "Importer un dossier"}
        </Button>
      )}
    </>
  );
};

export default ImportDocumentFolderButton;
